import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import toast from 'react-hot-toast';
import { DataTable } from '@/components/molecules';
import { Button, State } from '@/components/atoms';
import { cn } from '@/lib/utils';
import { downloadBillingReceipt } from '@/lib/billing/download-receipt';
import { Download, Receipt } from 'lucide-react';

export interface BillingHistoryItem {
  id: string;
  invoiceId: string;
  plan: string;
  amount: string;
  rawAmount: string;
  currency: string;
  paymentMethod: string;
  status: 'Paid' | 'Pending' | 'Failed';
  paidAt?: string | null;
  receiptUrl?: string | null;
  startDate: string;
  endDate: string;
  rawStartDate: string;
  rawEndDate: string;
  autoRenew: string;
}

interface BillingTableProps {
  data: BillingHistoryItem[];
  onView?: (billing: BillingHistoryItem) => void;
}

const handleDownloadReceipt = async (billing: BillingHistoryItem) => {
  if (billing.status !== 'Paid') {
    toast.error('Receipt is only available for paid invoices.');
    return;
  }

  try {
    await downloadBillingReceipt({
      invoiceId: billing.invoiceId,
      plan: billing.plan,
      amount: billing.rawAmount,
      currency: billing.currency,
      paymentMethod: billing.paymentMethod,
      status: billing.status,
      startDate: billing.rawStartDate,
      endDate: billing.rawEndDate,
      autoRenew: billing.autoRenew,
      paidAt: billing.paidAt,
      receiptUrl: billing.receiptUrl,
    });
  } catch {
    toast.error('Could not download receipt. Please try again.');
  }
};

export const BillingTable = ({ data }: BillingTableProps) => {
  const columns = useMemo<ColumnDef<BillingHistoryItem>[]>(
    () => [
      {
        accessorKey: 'invoiceId',
        header: () => (
          <div className="text-white font-semibold">Invoice ID</div>
        ),
      },
      {
        accessorKey: 'plan',
        header: () => <div className="text-white font-semibold">Plan</div>,
      },
      {
        accessorKey: 'amount',
        header: () => <div className="text-white font-semibold">Amount</div>,
        cell: ({ row }) => (
          <span className="font-medium">{row.original.amount}</span>
        ),
      },
      {
        accessorKey: 'paymentMethod',
        header: () => (
          <div className="text-white font-semibold">Payment Method</div>
        ),
      },
      {
        accessorKey: 'status',
        header: () => <div className="text-white font-semibold">Status</div>,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'size-2 rounded-full',
                row.original.status === 'Paid'
                  ? 'bg-green-500'
                  : row.original.status === 'Pending'
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
              )}
            />
            <span className="font-medium">{row.original.status}</span>
          </div>
        ),
      },
      {
        accessorKey: 'startDate',
        header: () => (
          <div className="text-white font-semibold">Start Date</div>
        ),
      },
      {
        accessorKey: 'endDate',
        header: () => <div className="text-white font-semibold">End Date</div>,
      },
      {
        accessorKey: 'autoRenew',
        header: () => (
          <div className="text-white font-semibold">Auto-Renew</div>
        ),
      },
      {
        id: 'actions',
        header: () => (
          <div className="text-white font-semibold uppercase text-xs">
            Actions
          </div>
        ),
        cell: ({ row }) => (
          <Button
            variant="outline"
            size="sm"
            className="text-xs gap-1.5"
            disabled={row.original.status !== 'Paid'}
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadReceipt(row.original);
            }}
          >
            <Download className="size-3.5" />
            Receipt
          </Button>
        ),
      },
    ],
    []
  );

  if (data.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center py-20 bg-white rounded-xl">
        <State
          description="You don't have any billing history yet"
          Icon={Receipt}
        />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-accent">
      <div className="min-w-full">
        <DataTable columns={columns} data={data} />
      </div>
    </div>
  );
};
